import { expiryText } from '@ims/core'
import { lineSerials } from './lib/serials.ts'
import { computeDocument, type DocLineInput, type KotLine } from '@ims/core'
import type { InvoiceDetail, InvoiceLineRow, InvoiceRow, RowOf, TenantSettingsRow } from '@ims/data'
import type { ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { balanceLine, type BillAdjustments } from './lib/bill-adjustments.ts'
import type { BillService } from './lib/orders.ts'
import { money, printDateLabel, qty } from './lib/format.ts'
import { AGES, rowWhat, type CustomerStanding, type Statement } from './lib/statement.ts'
import {
  billQrText,
  billView,
  lineDiscount,
  lineQtyText,
  lineRate,
  type BillView,
} from './lib/bill-view.ts'
import { billA4, type A4Doc } from './lib/doc-view.ts'
import { qrPath } from './qr.tsx'
import { billTitle, registrationOf, showsVat, taxNumberLabel } from './lib/registration.ts'

// Printing without a PDF library: the document renders into #print-root and
// the browser prints it (Print → Save as PDF gives a PDF). A4 for invoices,
// 80 mm for thermal receipt printers.

export type PrintFormat = 'a4' | 'receipt'

/**
 * The page for a receipt or a kitchen ticket. It names no size: a thermal printer's driver
 * offers its own paper (80 x 210 mm, or the 80 x 3276 mm roll), and Chrome centres a page of
 * any other size on that paper, which left blank paper above and below every bill. Portrait
 * always, because a short slip on a page of its own height (one dish, about 40 mm) is wider
 * than it is tall and Chrome then printed it sideways. Nothing above or below, so the slip
 * starts at the top of the paper; 4 mm each side centres the 72 mm slip on 80 mm paper.
 * A slip-sized PDF comes from the app's own Save as PDF (`makeBillPdf`), not from here.
 */
export const RECEIPT_PAGE = '@page { size: portrait; margin: 0 4mm; }'

/**
 * Prints `content`. `name` is what a saved PDF is called ("Receipt-INV-001"): the browser
 * names the file after the page's title, so the title is borrowed while the dialog is open.
 */
export function printDocument(content: ReactNode, format: PrintFormat, name?: string): void {
  let host = document.getElementById('print-root')
  if (!host) {
    host = document.createElement('div')
    host.id = 'print-root'
    document.body.appendChild(host)
  }
  const root = createRoot(host)
  flushSync(() => root.render(content))
  const style = document.createElement('style')
  style.textContent = format === 'receipt' ? RECEIPT_PAGE : '@page { size: A4; margin: 14mm; }'
  document.head.appendChild(style)
  document.body.dataset.printing = format
  const title = document.title
  if (name) document.title = name
  const cleanup = () => {
    delete document.body.dataset.printing
    style.remove()
    root.unmount()
    // Only put the title back if nothing else changed it meanwhile.
    if (name && document.title === name) document.title = title
    window.removeEventListener('afterprint', cleanup)
  }
  window.addEventListener('afterprint', cleanup)
  window.print()
}

/** The file name for a printed bill: "Receipt-INV-WEB-208384-00001", or "Receipt" before it has a number. */
export function printName(
  kind: 'Receipt' | 'Invoice' | 'Quotation' | 'Order' | 'Purchase-order',
  invoice: Pick<InvoiceRow, 'number'>,
): string {
  return invoice.number ? `${kind}-${invoice.number}` : kind
}

/** Batch number and expiry to print under a line (by batch id). */
export type BatchLabels = Record<string, { batch_no: string; expiry_date: string | null }>

interface DocProps {
  invoice: InvoiceRow
  lines: InvoiceLineRow[]
  settings: TenantSettingsRow | null | undefined
  balancePaisa: InvoiceDetail['balancePaisa']
  batches?: BatchLabels
  /** Credit notes and payments since the bill, printed under its totals (`billAdjustments`). */
  adjustments?: BillAdjustments | null
  /** "COPY OF ORIGINAL - N" under the title on a reprint (`nextPrint`); none on the original. */
  copyLabel?: string | null
  /** A restaurant bill's table and guests (or order number) and its server (`orderServiceFor`). */
  service?: BillService | null
  /** Who prints it and when, its order, the QR's link. */
  meta?: BillPrintMeta
}

/** "S/N: A1, A2" for a line that covered serial-tracked units. */
export function serialText(line: Pick<InvoiceLineRow, 'serials'>): string | null {
  const serials = lineSerials(line.serials)
  return serials.length > 0 ? `S/N: ${serials.join(', ')}` : null
}

export function batchText(
  line: Pick<InvoiceLineRow, 'batch_id'>,
  batches: BatchLabels | undefined,
): string | null {
  const b = line.batch_id ? batches?.[line.batch_id] : undefined
  if (!b) return null
  return `Batch ${b.batch_no}${b.expiry_date ? ` · Exp ${expiryText(b.expiry_date)}` : ''}`
}

type PrintSettings = DocProps['settings']

function title(invoice: InvoiceRow, settings: PrintSettings): string {
  return billTitle(registrationOf(settings), invoice.status)
}

function numberLabel(invoice: InvoiceRow): string {
  return invoice.number ?? (invoice.status === 'draft' ? 'Draft' : 'Number assigned on sync')
}

/** The shop's name and details at the top left of an A4 document. */
function BusinessBlock({ settings }: { settings: PrintSettings }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
      <LogoImage src={settings?.logo ?? null} height={56} width={150} />
      <div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{settings?.business_name}</div>
        {settings?.legal_name && <div>{settings.legal_name}</div>}
        {settings?.address && <div>{settings.address}</div>}
        {settings?.pan_vat_number && (
          <div>
            {taxNumberLabel(registrationOf(settings))}: {settings.pan_vat_number}
          </div>
        )}
        {(settings?.phone || settings?.email) && (
          <div>{[settings?.phone, settings?.email].filter(Boolean).join(' · ')}</div>
        )}
      </div>
    </div>
  )
}

/** What an A4 lines table needs: a bill line, or a quotation / order line (no batch, no serials). */
type A4Line = Pick<
  InvoiceLineRow,
  | 'id'
  | 'line_no'
  | 'description'
  | 'qty_milli'
  | 'unit_price_paisa'
  | 'discount_paisa'
  | 'tax_rate_bp'
  | 'total_paisa'
> &
  Partial<Pick<InvoiceLineRow, 'batch_id' | 'serials'>>

/** Batch and serial notes under a line (a quotation has neither). */
function extras(line: A4Line, batches: BatchLabels | undefined): string[] {
  return [
    batchText({ batch_id: line.batch_id ?? null }, batches),
    serialText({ serials: line.serials ?? null }),
  ].filter((text) => text !== null)
}

/** What a print needs beyond the bill: who prints it and when, its order, the QR's link. */
export interface BillPrintMeta {
  /** The sales order the bill came from (its number), shown as Reference. */
  reference?: string | null
  printedBy?: string | null
  /** ISO timestamp of the print. */
  printedAt?: string
  /** The api Worker's address (`platform.apiUrl`), for the QR's VERIFY link. */
  apiUrl?: string | null
}

/** Batch and serial notes per line id, for `billView`. */
export function lineNotesOf(
  lines: readonly InvoiceLineRow[],
  batches: BatchLabels | undefined,
): Map<string, string[]> {
  return new Map(lines.map((l) => [l.id, extras(l, batches)]))
}

/** The bill's model (lib/bill-view.ts) from a print's props. */
export function viewOf(props: DocProps & { paidPaisa?: number; changePaisa?: number }): BillView {
  return billView({
    invoice: props.invoice,
    lines: props.lines,
    settings: props.settings,
    balancePaisa: props.balancePaisa,
    lineNotes: lineNotesOf(props.lines, props.batches),
    adjustments: props.adjustments,
    copyLabel: props.copyLabel,
    service: props.service,
    paidPaisa: props.paidPaisa,
    changePaisa: props.changePaisa,
    ...props.meta,
  })
}

/** The business's logo as printed; nothing when it has none. Black and white for receipts. */
function LogoImage({
  src,
  height,
  width,
  mono,
}: {
  src: string | null
  height: number
  width?: number
  mono?: boolean
}) {
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      style={{
        display: 'block',
        maxHeight: height,
        maxWidth: width ?? height * 2.5,
        objectFit: 'contain',
        // A thermal printer has no grey: every pixel ends up black or white anyway.
        ...(mono ? { filter: 'grayscale(1) contrast(10)' } : {}),
      }}
    />
  )
}

/** The QR with the bill's details (`billQrText`), black on white. */
export function BillQr({ text, size }: { text: string; size: number }) {
  const { size: modules, path } = qrPath(text)
  const quiet = 2
  return (
    <svg
      role="img"
      aria-label="QR with the bill's details"
      viewBox={`${-quiet} ${-quiet} ${modules + quiet * 2} ${modules + quiet * 2}`}
      style={{ width: size, height: size, display: 'block', background: '#fff' }}
      shapeRendering="crispEdges"
    >
      <path d={path} fill="#000" />
    </svg>
  )
}

const HEAD_CELL = {
  padding: '7px 8px',
  background: '#6b7280',
  color: '#fff',
  fontWeight: 700,
  fontSize: 11.5,
  whiteSpace: 'nowrap',
} as const
const CELL = {
  padding: '6px 8px',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'top',
} as const

/** One signature line: "For: <business>" with its "For:" in bold. */
function Signature({ text, align }: { text: string; align: 'left' | 'right' }) {
  const own = text.startsWith('For: ')
  return (
    <div style={{ width: 230, textAlign: align }}>
      <div style={{ height: 56 }} />
      <div style={{ borderTop: '1.5px solid #111', paddingTop: 4 }}>
        {own ? (
          <>
            <b>For:</b> {text.slice(5)}
          </>
        ) : (
          text
        )}
      </div>
    </div>
  )
}

/**
 * Any document on A4 (lib/doc-view.ts; boards LogoBillA4, DocQuotePrint, DocPOPrint): name
 * and details left, the logo right, the title centred, who it is for beside its details, the
 * lines under a grey head, notes and the amount in words beside the totals, the QR (bills
 * only), who signs, and who printed it. a4-pdf.ts draws the same page as a file.
 */
export function A4Page({ doc }: { doc: A4Doc }) {
  const pair = (l: { label: string; value: string; strong?: boolean }, i: number) => (
    <div
      key={i}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '3px 6px',
        ...(l.strong ? { background: '#e5e7eb', fontWeight: 700, fontSize: 13, marginTop: 3 } : {}),
      }}
    >
      <span>{l.label}</span>
      <span style={{ whiteSpace: 'nowrap' }}>{l.value}</span>
    </div>
  )
  const own = doc.signatures.at(-1)
  const others = doc.signatures.slice(0, -1)
  return (
    <div style={{ font: '12px/1.5 system-ui, sans-serif', color: '#111' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 24,
          paddingBottom: 10,
          borderBottom: '1px solid #d1d5db',
        }}
      >
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{doc.seller.name}</div>
          {doc.seller.legalName && <div>{doc.seller.legalName}</div>}
          <div>{[doc.seller.address, doc.seller.taxLine].filter(Boolean).join(', ')}</div>
          {(doc.seller.phone || doc.seller.email) && (
            <div>
              {[
                doc.seller.phone && `Phone: ${doc.seller.phone}`,
                doc.seller.email && `Email: ${doc.seller.email}`,
              ]
                .filter(Boolean)
                .join(', ')}
            </div>
          )}
        </div>
        <LogoImage src={doc.seller.logo} height={64} width={170} />
      </div>
      <div style={{ margin: '12px 0 8px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, textTransform: 'uppercase' }}>{doc.title}</div>
        {doc.copyLabel && <div style={{ fontSize: 13, fontWeight: 700 }}>{doc.copyLabel}</div>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 32 }}>
        <div>
          <div style={{ fontWeight: 700 }}>{doc.party.heading}</div>
          {doc.party.lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
        <div style={{ minWidth: 280 }}>
          <div style={{ fontWeight: 700 }}>{doc.detailsHeading}</div>
          {doc.details.map((d) => (
            <div
              key={d.label}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}
            >
              <span>{d.label}:</span>
              <span>{d.value}</span>
            </div>
          ))}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, font: 'inherit' }}>
        <thead>
          <tr>
            <th style={{ ...HEAD_CELL, textAlign: 'left', width: 24 }}>#</th>
            {doc.columns.map((c) => (
              <th key={c.key} style={{ ...HEAD_CELL, textAlign: c.align }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {doc.rows.map((r) => (
            <tr key={r.no}>
              <td style={CELL}>{r.no}</td>
              {doc.columns.map((c) => (
                <td key={c.key} style={{ ...CELL, textAlign: c.align }}>
                  {r.cells[c.key]}
                  {c.key === 'item' &&
                    r.notes.map((n) => (
                      <div key={n} style={{ color: '#6b7280', fontStyle: 'italic', fontSize: 11 }}>
                        {n}
                      </div>
                    ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 32, marginTop: 12 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 14,
          }}
        >
          {doc.notes.map((n) => (
            <div key={n.heading}>
              <div style={{ fontWeight: 700 }}>{n.heading}</div>
              <div style={{ whiteSpace: 'pre-line' }}>{n.text}</div>
            </div>
          ))}
          {doc.notes.length === 0 && <span />}
          <div>
            <div style={{ fontWeight: 700 }}>Amount in words</div>
            <div>{doc.words}</div>
          </div>
        </div>
        <div style={{ width: 290, flex: 'none' }}>
          {doc.totals.map(pair)}
          {doc.credits.length > 0 && (
            <div
              style={{
                marginTop: 8,
                padding: '0 6px',
                fontSize: 10,
                textTransform: 'uppercase',
                color: '#555',
              }}
            >
              Returns and payments
            </div>
          )}
          {doc.credits.map(pair)}
          {doc.after.map(pair)}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 24,
          marginTop: 28,
          breakInside: 'avoid',
        }}
      >
        {doc.qrText ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <BillQr text={doc.qrText} size={92} />
            <div style={{ fontSize: 10.5, color: '#4b5563' }}>Scan to check this bill</div>
          </div>
        ) : (
          others.map((t) => <Signature key={t} text={t} align="left" />)
        )}
        {doc.qrText && others.map((t) => <Signature key={t} text={t} align="left" />)}
        {own && <Signature text={own} align="right" />}
      </div>
      {doc.notice && <p style={{ margin: '18px 0 0', color: '#4b5563' }}>{doc.notice}</p>}
      {doc.footer && <p style={{ margin: '14px 0 0' }}>{doc.footer}</p>}
      {doc.contact && <p style={{ margin: '6px 0 0', color: '#4b5563' }}>{doc.contact}</p>}
      {doc.printed && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 6,
            borderTop: '1px solid #e5e7eb',
            textAlign: 'right',
            fontSize: 10.5,
            color: '#4b5563',
          }}
        >
          {doc.printed}
        </div>
      )}
    </div>
  )
}

/** A bill on A4 (boards LogoBillA4, LogoBillA4Pan), from its model through `A4Page`. */
export function InvoiceA4(props: DocProps) {
  return <A4Page doc={billA4(viewOf(props))} />
}

/** A customer's statement on A4 (Print instead, in the statement sheet). The PDF file is drawn by statement-pdf.ts. */
export function StatementA4({
  statement,
  standing,
  customer,
  settings,
}: {
  statement: Statement
  standing: CustomerStanding
  customer: Pick<RowOf<'customers'>, 'name' | 'address' | 'phone' | 'pan_vat_number'>
  settings: PrintSettings
}) {
  const m = (p: number) => money(p, settings)
  const cell = { padding: '4px 6px', borderBottom: '1px solid #ddd' } as const
  const num = { ...cell, textAlign: 'right' as const, whiteSpace: 'nowrap' as const }
  return (
    <div style={{ font: '12px/1.45 system-ui, sans-serif', color: '#111' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
        <BusinessBlock settings={settings} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase' }}>
            Statement of account
          </div>
          <div>
            {printDateLabel(statement.from, settings)} – {printDateLabel(statement.to, settings)}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 700 }}>{customer.name}</div>
        {customer.address && <div>{customer.address}</div>}
        {(customer.phone || customer.pan_vat_number) && (
          <div>
            {[customer.phone, customer.pan_vat_number && `PAN ${customer.pan_vat_number}`]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr style={{ borderBottom: '1.5px solid #111', textAlign: 'left' }}>
            <th style={cell}>Date</th>
            <th style={cell}>What</th>
            <th style={{ ...num }}>Billed</th>
            <th style={{ ...num }}>Paid or credited</th>
            <th style={{ ...num }}>Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cell}>{printDateLabel(statement.from, settings)}</td>
            <td style={{ ...cell, fontWeight: 600 }} colSpan={3}>
              Balance before this period
            </td>
            <td style={num}>{m(statement.openingPaisa)}</td>
          </tr>
          {statement.rows.map((r) => (
            <tr key={r.id}>
              <td style={cell}>{printDateLabel(r.date, settings)}</td>
              <td style={cell}>
                {rowWhat(r)}{' '}
                {r.kind === 'payment'
                  ? [r.reference, r.aheadPaisa > 0 && 'paid ahead'].filter(Boolean).join(' · ')
                  : r.number}
              </td>
              <td style={num}>{r.billedPaisa ? m(r.billedPaisa) : ''}</td>
              <td style={num}>{r.paidPaisa ? m(r.paidPaisa) : ''}</td>
              <td style={num}>{m(r.balancePaisa)}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700, borderTop: '1.5px solid #111' }}>
            <td style={cell} colSpan={2}>
              Owed on {printDateLabel(statement.to, settings)}
            </td>
            <td style={num}>{m(statement.billedPaisa)}</td>
            <td style={num}>{m(statement.paidPaisa)}</td>
            <td style={{ ...num, fontSize: 14 }}>{m(statement.closingPaisa)}</td>
          </tr>
        </tbody>
      </table>
      {standing.openBills.length > 0 && (
        <>
          <div style={{ marginTop: 16, fontWeight: 700 }}>Bills still open</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {standing.openBills.map((b) => (
                <tr key={b.id}>
                  <td style={cell}>{b.number ?? '—'}</td>
                  <td style={cell}>{printDateLabel(b.date, settings)}</td>
                  <td style={cell}>
                    {b.dueDate ? `Due ${printDateLabel(b.dueDate, settings)}` : ''}
                    {b.lateDays > 0 ? ` · ${b.lateDays} days late` : ''}
                  </td>
                  <td style={num}>{m(b.openPaisa)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8 }}>
            {AGES.map((label, i) => `${label}: ${m(standing.ageing[i] ?? 0)}`).join(' · ')}
            {standing.paidAheadPaisa > 0 && ` · Paid ahead: ${m(standing.paidAheadPaisa)}`}
          </div>
        </>
      )}
    </div>
  )
}

/** 80 mm thermal receipt. */
export function Receipt({
  invoice,
  lines,
  settings,
  paidPaisa,
  changePaisa,
  batches,
  adjustments,
  copyLabel,
  service,
  meta,
}: DocProps & { paidPaisa?: number; changePaisa?: number }) {
  const m = (p: number) => money(p, settings)
  const row = { display: 'flex', justifyContent: 'space-between', gap: 8 } as const
  const qrText = billQrText(invoice, settings, meta?.apiUrl)
  return (
    <div style={{ font: '11px/1.35 ui-monospace, monospace', color: '#000', width: '72mm' }}>
      <div style={{ textAlign: 'center' }}>
        {settings?.logo_on_receipt !== false && settings?.logo && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
            <LogoImage src={settings.logo} height={52} width={132} mono />
          </div>
        )}
        <div style={{ fontWeight: 700, fontSize: 13 }}>{settings?.business_name}</div>
        {settings?.address && <div>{settings.address}</div>}
        {settings?.pan_vat_number && (
          <div>
            {taxNumberLabel(registrationOf(settings))}: {settings.pan_vat_number}
          </div>
        )}
        <div style={{ marginTop: 4, fontWeight: 700 }}>
          {title(invoice, settings).toUpperCase()}
        </div>
        {copyLabel && <div style={{ fontWeight: 700 }}>{copyLabel}</div>}
      </div>
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      <div>No: {numberLabel(invoice)}</div>
      <div>Date: {printDateLabel(invoice.invoice_date, settings)}</div>
      {invoice.customer_name && <div>Customer: {invoice.customer_name}</div>}
      {service && (
        <>
          <div style={{ ...row, fontWeight: 700 }}>
            <span>{service.heading}</span>
            {service.right && <span>{service.right}</span>}
          </div>
          {service.extra && <div>{service.extra}</div>}
          {service.servedBy && <div>Served by: {service.servedBy}</div>}
        </>
      )}
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      {lines.map((l) => (
        <div key={l.id} style={{ marginBottom: 2 }}>
          <div>{l.description}</div>
          {batchText(l, batches) && <div style={{ fontSize: 10 }}>{batchText(l, batches)}</div>}
          {serialText(l) && <div style={{ fontSize: 10 }}>{serialText(l)}</div>}
          <div style={row}>
            <span>
              {lineQtyText(l)} × {m(lineRate(l))}
              {lineDiscount(l) ? ` −${m(lineDiscount(l))}` : ''}
            </span>
            <span>{m(l.total_paisa)}</span>
          </div>
        </div>
      ))}
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      {showsVat(registrationOf(settings), invoice.tax_paisa) && (
        <>
          <div style={row}>
            <span>Taxable</span>
            <span>{m(invoice.taxable_paisa)}</span>
          </div>
          <div style={row}>
            <span>VAT</span>
            <span>{m(invoice.tax_paisa)}</span>
          </div>
        </>
      )}
      {invoice.round_off_paisa !== 0 && (
        <div style={row}>
          <span>Round off</span>
          <span>{m(invoice.round_off_paisa)}</span>
        </div>
      )}
      <div style={{ ...row, fontWeight: 700, fontSize: 13 }}>
        <span>TOTAL</span>
        <span>{m(invoice.total_paisa)}</span>
      </div>
      {paidPaisa !== undefined && (
        <div style={row}>
          <span>Paid</span>
          <span>{m(paidPaisa)}</span>
        </div>
      )}
      {changePaisa !== undefined && changePaisa > 0 && (
        <div style={row}>
          <span>Change</span>
          <span>{m(changePaisa)}</span>
        </div>
      )}
      {adjustments && (
        // After the bill, apart from it: the invoice's own totals above never change.
        <>
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          <div style={{ fontWeight: 700 }}>RETURNS AND PAYMENTS</div>
          {adjustments.credits.map((c, i) => (
            // 72 mm is too narrow for the number, the date and the amount on one line.
            <div key={i}>
              <div>Credit note {c.number ?? '(number on sync)'}</div>
              <div style={row}>
                <span>{printDateLabel(c.note_date, settings)}</span>
                <span style={{ whiteSpace: 'nowrap' }}>−{m(c.total_paisa)}</span>
              </div>
            </div>
          ))}
          <div style={row}>
            <span>Paid</span>
            <span>{m(adjustments.paidPaisa)}</span>
          </div>
          <div style={{ ...row, fontWeight: 700 }}>
            <span>{balanceLine(adjustments.balancePaisa)}</span>
            <span>{m(Math.abs(adjustments.balancePaisa))}</span>
          </div>
        </>
      )}
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      {invoice.notes && <div style={{ marginBottom: 4 }}>Note: {invoice.notes}</div>}
      {invoice.status !== 'draft' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            margin: '4px 0',
          }}
        >
          <BillQr text={qrText} size={110} />
          <div style={{ fontSize: 10 }}>Scan to check this bill</div>
        </div>
      )}
      <div style={{ textAlign: 'center' }}>{settings?.invoice_footer || 'Thank you!'}</div>
      {meta?.printedAt && (
        <div style={{ textAlign: 'center', fontSize: 10, marginTop: 2 }}>
          {meta.printedBy ? `Printed by ${meta.printedBy}` : 'Printed'}
        </div>
      )}
    </div>
  )
}

/** A kitchen ticket as printed. */
export interface KotSlipData {
  kotNo: number
  /** "KOT 12" or "BOT 13" (`ticketName`), printed large. */
  name: string
  station: string
  tableName: string | null
  time: string
  lines: KotLine[]
  /** A reprint: when it was printed again; the slip then says COPY. */
  copy?: string
}

/** Kitchen tickets (80 mm), one per page so the printer cuts between stations. */
export function KotSlips({ slips }: { slips: KotSlipData[] }) {
  return (
    <div style={{ font: '13px/1.35 ui-monospace, monospace', color: '#000', width: '72mm' }}>
      {slips.map((slip, i) => (
        <div
          key={`${slip.station}-${slip.kotNo}`}
          style={i < slips.length - 1 ? { breakAfter: 'page', pageBreakAfter: 'always' } : {}}
        >
          {slip.copy && (
            <div
              style={{
                textAlign: 'center',
                fontWeight: 800,
                letterSpacing: '0.3em',
                border: '2px solid #000',
                padding: 2,
                marginBottom: 4,
              }}
            >
              COPY
            </div>
          )}
          <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 24, lineHeight: 1.1 }}>
            {slip.name}
          </div>
          <div style={{ textAlign: 'center', marginBottom: 4 }}>{slip.station}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>{slip.tableName ?? 'No table'}</span>
            <span>{slip.time}</span>
          </div>
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          {slip.lines.map((l, n) => (
            <div key={n} style={{ marginBottom: 3 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                {l.cancelled ? 'CANCEL ' : ''}
                {qty(l.qtyMilli)} × {l.description}
              </div>
              {l.note && <div style={{ paddingLeft: 12 }}>{l.note}</div>}
            </div>
          ))}
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          {slip.copy && <div style={{ fontSize: 11 }}>Printed again {slip.copy}</div>}
        </div>
      ))}
    </div>
  )
}

/**
 * The guest check (80 mm): what the table has had so far, before payment.
 * It is not the bill: the invoice is made, numbered and printed at checkout.
 */
export function GuestCheck({
  lines,
  tableName,
  covers,
  date,
  time,
  settings,
}: {
  lines: readonly DocLineInput[]
  tableName: string | null
  covers: number
  /** Today, as an ISO date. */
  date: string
  time: string
  settings: PrintSettings
}) {
  const m = (p: number) => money(p, settings)
  const row = { display: 'flex', justifyContent: 'space-between', gap: 8 } as const
  const doc = computeDocument(lines, { roundOff: 'nearest_rupee' })
  return (
    <div style={{ font: '11px/1.35 ui-monospace, monospace', color: '#000', width: '72mm' }}>
      <div style={{ textAlign: 'center' }}>
        {settings?.logo_on_receipt !== false && settings?.logo && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
            <LogoImage src={settings.logo} height={52} width={132} mono />
          </div>
        )}
        <div style={{ fontWeight: 700, fontSize: 13 }}>{settings?.business_name}</div>
        {settings?.address && <div>{settings.address}</div>}
        <div style={{ marginTop: 4, fontWeight: 700 }}>GUEST CHECK</div>
      </div>
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      <div style={row}>
        <span>
          {tableName ?? 'No table'} · {covers} {covers === 1 ? 'guest' : 'guests'}
        </span>
        <span>{time}</span>
      </div>
      <div>Date: {printDateLabel(date, settings)}</div>
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      {lines.map((l, i) => (
        <div key={i} style={{ marginBottom: 2 }}>
          <div>{l.description}</div>
          <div style={row}>
            <span>
              {qty(l.qtyMilli)} × {m(l.unitPricePaisa)}
            </span>
            <span>{m(doc.lines[i]!.totalPaisa)}</span>
          </div>
        </div>
      ))}
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      {showsVat(registrationOf(settings), doc.totals.taxPaisa) && (
        <>
          <div style={row}>
            <span>Taxable</span>
            <span>{m(doc.totals.taxablePaisa)}</span>
          </div>
          <div style={row}>
            <span>VAT</span>
            <span>{m(doc.totals.taxPaisa)}</span>
          </div>
        </>
      )}
      {doc.totals.roundOffPaisa !== 0 && (
        <div style={row}>
          <span>Round off</span>
          <span>{m(doc.totals.roundOffPaisa)}</span>
        </div>
      )}
      <div style={{ ...row, fontWeight: 700, fontSize: 13 }}>
        <span>TOTAL</span>
        <span>{m(doc.totals.totalPaisa)}</span>
      </div>
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      <div style={{ textAlign: 'center' }}>Not a bill. Your bill is printed when you pay.</div>
    </div>
  )
}
